import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const KIND_OPTIONS = [
  { value: 'fixed', label: 'Fixes Datum (Monat/Tag)' },
  { value: 'easter', label: 'Ostern (Offset in Tagen)' },
  { value: 'nthWeekdayOfMonth', label: 'n-ter Wochentag im Monat' },
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sonntag' },
  { value: 1, label: 'Montag' },
  { value: 2, label: 'Dienstag' },
  { value: 3, label: 'Mittwoch' },
  { value: 4, label: 'Donnerstag' },
  { value: 5, label: 'Freitag' },
  { value: 6, label: 'Samstag' },
];

function clampInt(n, { min, max, fallback = null }) {
  const x = Number.parseInt(String(n), 10);
  if (!Number.isFinite(x)) return fallback;
  if (typeof min === 'number' && x < min) return min;
  if (typeof max === 'number' && x > max) return max;
  return x;
}

function ruleLabel(it) {
  if (!it) return '—';
  if ((it.kind || 'fixed') === 'easter') {
    const so = Number(it.startOffsetDays ?? 0);
    const eo = Number(it.endOffsetDays ?? it.startOffsetDays ?? 0);
    const fmt = (v) => (v > 0 ? `+${v}` : `${v}`);
    return `Ostern ${fmt(so)}d – ${fmt(eo)}d`;
  }
  if ((it.kind || 'fixed') === 'nthWeekdayOfMonth') {
    const month = Number(it.nthMonth);
    const weekday = Number(it.nthWeekday);
    const index = Number(it.nthIndex);
    if (!Number.isFinite(month) || !Number.isFinite(weekday) || !Number.isFinite(index)) return '—';
    const wd = WEEKDAY_OPTIONS.find(o => o.value === weekday)?.label || String(weekday);
    const idx = index === -1 ? 'letzter' : `${index}.`;
    const m = String(month).padStart(2, '0');
    return `${idx} ${wd} im ${m}`;
  }
  const sm = it.startMonth;
  const sd = it.startDay;
  const em = it.endMonth ?? it.startMonth;
  const ed = it.endDay ?? it.startDay;
  if (!sm || !sd) return '—';
  return `${String(sm).padStart(2, '0')}.${String(sd).padStart(2, '0')} – ${String(em).padStart(2, '0')}.${String(ed).padStart(2, '0')}`;
}

export default function SeasonalEvents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const [eggOverview, setEggOverview] = useState(null);
  const [eggOverviewLoading, setEggOverviewLoading] = useState(false);
  const [eggResetLoading, setEggResetLoading] = useState(false);

  const empty = useMemo(() => ({
    key: '',
    name: '',
    kind: 'fixed',
    enabled: true,
    order: 100,
    startMonth: 1,
    startDay: 1,
    endMonth: 1,
    endDay: 1,
    startOffsetDays: 0,
    endOffsetDays: 0,
    nthMonth: 5,
    nthWeekday: 0,
    nthIndex: 2,
    durationDays: 0,
    notes: '',
  }), []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/seasonal-events');
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(list);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Laden fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => setEditing({ ...empty });

  const startEdit = (it) => {
    const kind = it?.kind || 'fixed';
    setEditing({
      id: it.id,
      key: it.key || '',
      name: it.name || '',
      kind,
      enabled: it.enabled !== false,
      order: Number.isFinite(Number(it.order)) ? Number(it.order) : 100,
      startMonth: it.startMonth ?? 1,
      startDay: it.startDay ?? 1,
      endMonth: it.endMonth ?? (it.startMonth ?? 1),
      endDay: it.endDay ?? (it.startDay ?? 1),
      startOffsetDays: it.startOffsetDays ?? 0,
      endOffsetDays: it.endOffsetDays ?? (it.startOffsetDays ?? 0),
      nthMonth: it.nthMonth ?? 5,
      nthWeekday: it.nthWeekday ?? 0,
      nthIndex: it.nthIndex ?? 2,
      durationDays: it.durationDays ?? 0,
      notes: it.notes || '',
    });
  };

  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        key: String(editing.key || '').trim(),
        name: String(editing.name || '').trim(),
        kind: editing.kind || 'fixed',
        enabled: !!editing.enabled,
        order: clampInt(editing.order, { min: 0, max: 999, fallback: 100 }),
        durationDays: clampInt(editing.durationDays, { min: 0, max: 31, fallback: 0 }),
        notes: String(editing.notes || '').trim() || null,
      };

      if (!payload.key) throw new Error('Key erforderlich');
      if (!payload.name) throw new Error('Name erforderlich');

      if (payload.kind === 'fixed') {
        payload.startMonth = clampInt(editing.startMonth, { min: 1, max: 12, fallback: null });
        payload.startDay = clampInt(editing.startDay, { min: 1, max: 31, fallback: null });
        payload.endMonth = clampInt(editing.endMonth, { min: 1, max: 12, fallback: payload.startMonth });
        payload.endDay = clampInt(editing.endDay, { min: 1, max: 31, fallback: payload.startDay });
      } else if (payload.kind === 'easter') {
        payload.startOffsetDays = clampInt(editing.startOffsetDays, { min: -120, max: 120, fallback: null });
        payload.endOffsetDays = clampInt(editing.endOffsetDays, { min: -120, max: 120, fallback: payload.startOffsetDays });
      } else if (payload.kind === 'nthWeekdayOfMonth') {
        payload.nthMonth = clampInt(editing.nthMonth, { min: 1, max: 12, fallback: null });
        payload.nthWeekday = clampInt(editing.nthWeekday, { min: 0, max: 6, fallback: null });
        const idx = clampInt(editing.nthIndex, { min: -1, max: 5, fallback: null });
        payload.nthIndex = idx;
      }

      let res;
      if (editing.id) {
        res = await api.put(`/seasonal-events/${editing.id}`, payload);
        const updated = res.data;
        setItems(items.map(i => (Number(i.id) === Number(updated.id) ? updated : i)));
      } else {
        res = await api.post('/seasonal-events', payload);
        const created = res.data;
        setItems([created, ...items]);
      }
      setEditing(null);
      // reload to ensure consistent ordering
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!id) return;
    if (!window.confirm('Ereignis wirklich löschen?')) return;
    setSaving(true);
    setError('');
    try {
      await api.delete(`/seasonal-events/${id}`);
      setItems(items.filter(i => Number(i.id) !== Number(id)));
      if (editing?.id && Number(editing.id) === Number(id)) setEditing(null);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Löschen fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const loadEggOverview = async () => {
    setEggOverviewLoading(true);
    try {
      const res = await api.get('/easter-eggs/overview');
      setEggOverview(res.data || null);
    } catch (e) {
      setEggOverview(null);
      setError(e?.response?.data?.error || e?.message || 'Auswertung konnte nicht geladen werden.');
    } finally {
      setEggOverviewLoading(false);
    }
  };

  const resetEggOverview = async () => {
    if (eggResetLoading) return;
    const ok = window.confirm('Oster-Eier wirklich für ALLE Nutzer zurücksetzen?\n\nHinweis: Bereits vergebene Punkte werden dabei NICHT zurückgenommen.');
    if (!ok) return;
    setEggResetLoading(true);
    setError('');
    try {
      await api.post('/easter-eggs/reset', {});
      await loadEggOverview();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Zurücksetzen fehlgeschlagen.');
    } finally {
      setEggResetLoading(false);
    }
  };

  return (
    <div className="home-page">
      <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
        <h1 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Saisonale Ereignisse</h1>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 760 }}>
          Wiederkehrende Ereignisse (z.B. Feiertage) anlegen und verwalten.
        </p>
      </section>

      <section style={{ width: '100%', maxWidth: 900, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {error && (
          <div style={{ color: '#ff8484', marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
          <button onClick={startCreate} disabled={saving} style={{ background: '#2b2b2b', color: '#fff', border: '1px solid #444', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            Neu anlegen
          </button>
          <button onClick={load} disabled={loading || saving} style={{ background: '#2f2f2f', color: '#ddd', border: '1px solid #555', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', opacity: (loading || saving) ? 0.6 : 1 }}>
            Aktualisieren
          </button>
        </div>

        {loading ? (
          <div style={{ opacity: 0.8 }}>Lade…</div>
        ) : (
          <div style={{ background: '#151515', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 12 }}>
            {items.length === 0 ? (
              <div style={{ opacity: 0.75 }}>Noch keine Ereignisse angelegt.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Key</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Regel</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Aktiv</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '8px 10px', fontSize: 13, opacity: 0.9 }}>{it.key}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13 }}>{it.name}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13, opacity: 0.9 }}>{ruleLabel(it)}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13 }}>{it.enabled ? 'Ja' : 'Nein'}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13 }}>
                        <button onClick={() => startEdit(it)} disabled={saving} style={{ background: '#1f2744', color: '#9fbaff', border: '1px solid #2f3e66', padding: '6px 10px', borderRadius: 10, cursor: 'pointer', fontSize: 12, opacity: saving ? 0.6 : 1 }}>
                          Bearbeiten
                        </button>
                        <button onClick={() => remove(it.id)} disabled={saving} style={{ marginLeft: 8, background: '#441f20', color: '#ff8484', border: '1px solid #662d30', padding: '6px 10px', borderRadius: 10, cursor: 'pointer', fontSize: 12, opacity: saving ? 0.6 : 1 }}>
                          Löschen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {editing && (
          <div style={{ marginTop: 16, background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>{editing.id ? 'Ereignis bearbeiten' : 'Ereignis anlegen'}</h3>
              <button onClick={cancelEdit} disabled={saving} style={{ background: '#2f2f2f', color: '#ddd', border: '1px solid #555', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                Abbrechen
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <label>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Key</div>
                <input
                  value={editing.key}
                  onChange={(e) => setEditing({ ...editing, key: e.target.value })}
                  placeholder="z.B. christmas"
                  disabled={saving}
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                  autoComplete="off"
                />
              </label>
              <label>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Name</div>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="z.B. Weihnachten"
                  disabled={saving}
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                  autoComplete="off"
                />
              </label>

              <label>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Typ</div>
                <select
                  value={editing.kind}
                  onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                  disabled={saving}
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                >
                  {KIND_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Sortierung</div>
                <input
                  type="number"
                  min={0}
                  max={999}
                  value={editing.order}
                  onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                  disabled={saving}
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                />
              </label>

              <label>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Dauer (Tage)</div>
                <input
                  type="number"
                  min={0}
                  max={31}
                  value={editing.durationDays}
                  onChange={(e) => setEditing({ ...editing, durationDays: e.target.value })}
                  disabled={saving}
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                />
              </label>
              <div style={{ fontSize: 12, opacity: 0.65, display: 'flex', alignItems: 'flex-end' }}>
                0 = nur dieser Tag
              </div>

              {editing.kind === 'fixed' ? (
                <>
                  <div style={{ gridColumn: '1 / span 2', fontSize: 12, opacity: 0.75 }}>Zeitraum (Monat/Tag)</div>
                  <label>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Start Monat / Tag</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <input
                        type="number"
                        min={1}
                        max={12}
                        value={editing.startMonth}
                        onChange={(e) => setEditing({ ...editing, startMonth: e.target.value })}
                        disabled={saving}
                        style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                      />
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={editing.startDay}
                        onChange={(e) => setEditing({ ...editing, startDay: e.target.value })}
                        disabled={saving}
                        style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                      />
                    </div>
                  </label>
                  <label>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Ende Monat / Tag</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <input
                        type="number"
                        min={1}
                        max={12}
                        value={editing.endMonth}
                        onChange={(e) => setEditing({ ...editing, endMonth: e.target.value })}
                        disabled={saving}
                        style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                      />
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={editing.endDay}
                        onChange={(e) => setEditing({ ...editing, endDay: e.target.value })}
                        disabled={saving}
                        style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                      />
                    </div>
                  </label>
                </>
              ) : editing.kind === 'easter' ? (
                <>
                  <div style={{ gridColumn: '1 / span 2', fontSize: 12, opacity: 0.75 }}>Zeitraum (Offset in Tagen zu Ostersonntag)</div>
                  <label>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Start-Offset</div>
                    <input
                      type="number"
                      min={-120}
                      max={120}
                      value={editing.startOffsetDays}
                      onChange={(e) => setEditing({ ...editing, startOffsetDays: e.target.value })}
                      disabled={saving}
                      style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                    />
                  </label>
                  <label>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Ende-Offset</div>
                    <input
                      type="number"
                      min={-120}
                      max={120}
                      value={editing.endOffsetDays}
                      onChange={(e) => setEditing({ ...editing, endOffsetDays: e.target.value })}
                      disabled={saving}
                      style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                    />
                  </label>
                </>
              ) : (
                <>
                  <div style={{ gridColumn: '1 / span 2', fontSize: 12, opacity: 0.75 }}>Regel (n-ter Wochentag im Monat)</div>
                  <label>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Monat</div>
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={editing.nthMonth}
                      onChange={(e) => setEditing({ ...editing, nthMonth: e.target.value })}
                      disabled={saving}
                      style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                    />
                  </label>
                  <label>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Wochentag</div>
                    <select
                      value={editing.nthWeekday}
                      onChange={(e) => setEditing({ ...editing, nthWeekday: e.target.value })}
                      disabled={saving}
                      style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                    >
                      {WEEKDAY_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Index</div>
                    <select
                      value={editing.nthIndex}
                      onChange={(e) => setEditing({ ...editing, nthIndex: e.target.value })}
                      disabled={saving}
                      style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}
                    >
                      <option value={1}>1. im Monat</option>
                      <option value={2}>2. im Monat</option>
                      <option value={3}>3. im Monat</option>
                      <option value={4}>4. im Monat</option>
                      <option value={5}>5. im Monat</option>
                      <option value={-1}>letzter im Monat</option>
                    </select>
                  </label>
                  <div style={{ fontSize: 12, opacity: 0.65, display: 'flex', alignItems: 'flex-end' }}>
                    Beispiel: Muttertag = 2. Sonntag im Mai
                  </div>
                </>
              )}

              <label style={{ gridColumn: '1 / span 2' }}>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Notizen (optional)</div>
                <textarea
                  rows={3}
                  value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  disabled={saving}
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 10, padding: '10px 12px', fontSize: 13, resize: 'vertical' }}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, gridColumn: '1 / span 2', fontSize: 13, opacity: 0.9 }}>
                <input type="checkbox" checked={!!editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} disabled={saving} />
                aktiviert
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={save} disabled={saving} style={{ background: '#32CD89', color: '#032d1e', fontWeight: 800, border: '1px solid #32CD89', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Speichere…' : 'Speichern'}
              </button>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                Tipp: Für Weihnachten z.B. Start 12/24, Ende 12/26.
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 18, background: '#151515', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 800 }}>Oster‑Auswertung</div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Übersicht: wie viele Ostereier pro Nutzer gefunden wurden.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={loadEggOverview}
                disabled={eggOverviewLoading || eggResetLoading}
                style={{ background: '#2f2f2f', color: '#ddd', border: '1px solid #555', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', opacity: (eggOverviewLoading || eggResetLoading) ? 0.6 : 1 }}
              >
                {eggOverviewLoading ? 'Lade…' : 'Auswertung laden'}
              </button>
              <button
                onClick={resetEggOverview}
                disabled={eggOverviewLoading || eggResetLoading}
                style={{ background: '#441f20', color: '#ff8484', border: '1px solid #662d30', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', opacity: (eggOverviewLoading || eggResetLoading) ? 0.6 : 1 }}
              >
                {eggResetLoading ? 'Setze zurück…' : 'Zurücksetzen'}
              </button>
            </div>
          </div>

          {eggOverview && Array.isArray(eggOverview.items) && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>
                Status: {eggOverview?.live?.active ? 'LIVE' : 'nicht live'}
                {eggOverview?.live?.window?.startAt && eggOverview?.live?.window?.endAt && (
                  <span style={{ marginLeft: 10 }}>
                    (Zeitraum: {new Date(eggOverview.live.window.startAt).toLocaleDateString()} – {new Date(eggOverview.live.window.endAt).toLocaleDateString()})
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>
                Vollständig: {Array.isArray(eggOverview.completedUserIds) ? eggOverview.completedUserIds.length : 0} Nutzer
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Nutzer</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Gefunden</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Vollständig</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 12, opacity: 0.8 }}>Zuletzt</th>
                  </tr>
                </thead>
                <tbody>
                  {eggOverview.items.map((it) => (
                    <tr key={it.userId} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '8px 10px', fontSize: 13 }}>
                        {it.displayName || it.username}
                        <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.7 }}>#{it.userId}</span>
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', fontWeight: 700 }}>
                        {it.foundCount} / {it.total}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 13 }}>
                        {it.complete ? 'Ja' : 'Nein'}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 13, opacity: 0.9 }}>
                        {it.lastFoundAt ? new Date(it.lastFoundAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
